import { useMemo } from 'react';

import { cn } from '~/utils/cn';

import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';

interface PropsValidatePassword {
  value: string;
  className?: string;
}

interface PasswordRule {
  label: string;
  test: (v: string) => boolean;
}

const RULES: PasswordRule[] = [
  { label: '8 caracteres', test: (v) => v.length >= 8 },
  { label: 'Letra maiúscula', test: (v) => /[A-Z]/.test(v) },
  { label: 'Número', test: (v) => /\d/.test(v) },
  {
    label: 'Caractere especial',
    test: (v) => /[!@#$%^&*()_+\-={}[\]:";'<>?,./]/.test(v),
  },
];

type Strength = {
  level: 0 | 1 | 2 | 3 | 4;
  label: string;
  segmentClass: string;
  labelClass: string;
};

function strengthFor(score: number): Strength {
  switch (score) {
    case 0:
      return {
        level: 0,
        label: 'Vamos começar',
        segmentClass: 'bg-intense-03',
        labelClass: 'color-intense-05',
      };
    case 1:
      return {
        level: 1,
        label: 'Fraca',
        segmentClass: 'bg-warning-04',
        labelClass: 'color-warning-06',
      };
    case 2:
      return {
        level: 2,
        label: 'Razoável',
        segmentClass: 'bg-alert-04',
        labelClass: 'color-alert-06',
      };
    case 3:
      return {
        level: 3,
        label: 'Boa',
        segmentClass: 'bg-success-04',
        labelClass: 'color-success-06',
      };
    default:
      return {
        level: 4,
        label: 'Excelente',
        segmentClass: 'bg-success-05',
        labelClass: 'color-success-06',
      };
  }
}

export default function ValidatePassword({
  value,
  className,
}: PropsValidatePassword) {
  const results = useMemo(() => RULES.map((r) => r.test(value)), [value]);
  const score = results.filter(Boolean).length;
  const strength = strengthFor(score);

  return (
    <div
      className={cn(
        'rounded-medium border border-intense-03 bg-intense-02 p-smaller',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <Text className="text-captionLG color-intense-06 uppercase tracking-wide">
          Força da senha
        </Text>
        <Text className={cn('text-captionLG font-medium', strength.labelClass)}>
          {strength.label}
        </Text>
      </div>

      <div className="mt-quark flex gap-quark" role="progressbar" aria-valuemin={0} aria-valuemax={RULES.length} aria-valuenow={score}>
        {RULES.map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-[6px] flex-1 rounded-full transition-colors duration-200',
              i < score ? strength.segmentClass : 'bg-intense-03',
            )}
          />
        ))}
      </div>

      <ul className="mt-smaller grid grid-cols-2 gap-quark">
        {RULES.map((r, i) => {
          const ok = results[i];
          return (
            <li
              key={r.label}
              className={cn(
                'flex items-center gap-quark rounded-small px-quark py-quark transition-colors',
                ok ? 'bg-success-01' : 'bg-intense-01',
              )}
            >
              <Icon
                name={ok ? 'icon-circle-check' : 'icon-circle-close'}
                size={14}
                color={ok ? '#00963D' : '#B0B0B0'}
                rounded={false}
              />
              <Text
                className={cn(
                  'text-captionMD',
                  ok ? 'color-success-06' : 'color-intense-05',
                )}
              >
                {r.label}
              </Text>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export const isPasswordStrong = (value: string) =>
  RULES.every((r) => r.test(value));
